import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowRight,
  CheckCircle2,
  GitBranch,
  History,
  Plus,
  Trash2,
} from "lucide-react";
import {
  createNormSchema,
  supersedeNormSchema,
  type CreateNormInput,
  type NormDto,
  type NormHistoryItemDto,
  type NormRecommendationDto,
  type SupersedeNormInput,
} from "@normax-performance/shared-types";
import { toIsoDate } from "@normax-performance/shared-format";
import { catalogApi, companyPerformanceSettingsApi, normRecommendationsApi, normsApi } from "../../lib/api-client/endpoints";
import { ApiError } from "../../lib/api-client/client";
import {
  Badge,
  Button,
  CatalogToolbar,
  DateField,
  Input,
  Modal,
  OverflowMenu,
  SearchCombobox,
  SortableColumnHeader,
  useSortableRows,
  useToast,
  type ComboboxOption,
  type OverflowMenuItem,
} from "../../shared-ui/primitives";
import { useCatalogFilter } from "../../shared-ui/hooks/useCatalogFilter";
import { formatCodeAndName, formatDateSr } from "../../lib/format";
import "./NormsTab.css";

/** Rounds to a whole number and groups it with "." every 3 digits (e.g.
 * "8.484") — a single fixed format regardless of active UI language, same
 * contract as `formatDateSr`'s dd.mm.yyyy (see lib/format/intl-format.ts's
 * doc comment: user-visible numbers/dates here are not locale-dependent).
 * Every raw count/quantity/minutes value in this table goes through this,
 * never shown as a bare unformatted number. */
function formatNum(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = Math.round(Number(value));
  if (Number.isNaN(n)) return "—";
  const sign = n < 0 ? "-" : "";
  const grouped = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${grouped}`;
}

/** History sub-rows have both a start and end date; the collapsed table's
 * "Važi od" column has no spare neighbor to show validTo in (unlike the
 * old layout), so both dates share this one cell. */
function formatValidRange(validFrom: string | null, validTo: string | null): string {
  if (!validFrom) return "—";
  return validTo ? `${formatDateSr(validFrom)} – ${formatDateSr(validTo)}` : formatDateSr(validFrom);
}

/**
 * Groups the flat norm list (every version of every product+operation
 * combination) into one row per combination — the "current" version plus
 * its read-only history — so the table shows what Daily Entry actually
 * resolves today, not a flat, hard-to-scan version list.
 */
export interface NormGroup {
  key: string;
  productId: string | null;
  productName: string | null;
  operationId: string;
  operationName: string;
  current: NormDto | null;
  history: NormDto[];
}

export function groupNorms(norms: NormDto[]): NormGroup[] {
  const groups = new Map<string, NormGroup>();
  for (const n of norms) {
    const key = `${n.productId ?? "none"}::${n.operationId}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        productId: n.productId,
        productName: n.productName,
        operationId: n.operationId,
        operationName: n.operationName,
        current: null,
        history: [],
      };
      groups.set(key, group);
    }
    if (n.status === "current") {
      group.current = n;
    } else {
      group.history.push(n);
    }
  }
  for (const group of groups.values()) {
    group.history.sort((a, b) => (b.validFrom ?? "").localeCompare(a.validFrom ?? ""));
  }
  return [...groups.values()].sort((a, b) => a.operationName.localeCompare(b.operationName));
}

type NormFormValues = {
  productId: string;
  operationId: string;
  standardQuantity: string;
  unitId: string;
  effectiveFrom: string;
  applyRetroactively: boolean;
  retroactiveReason: string;
};

function buildEmptyForm(): NormFormValues {
  return {
    productId: "",
    operationId: "",
    standardQuantity: "",
    unitId: "",
    effectiveFrom: toIsoDate(new Date()),
    applyRetroactively: false,
    retroactiveReason: "",
  };
}

type FormState =
  | { mode: "create" }
  | { mode: "supersede"; group: NormGroup; recommendedStandardQuantity?: string }
  | null;

/**
 * Plain local state, not react-hook-form — SearchCombobox (Product/
 * Operation/Unit) is a controlled selection component, not a native input,
 * the same reason Daily Entry's row components (NormedWorkRow.tsx etc.) use
 * local state instead of `register()`. Validated on submit via the same Zod
 * schemas the backend uses, so the two never drift.
 */
function NormFormModal({
  mode,
  group,
  productOptions,
  operationOptions,
  unitOptions,
  recommendedStandardQuantity,
  onClose,
  onSubmit,
  isPending,
}: {
  mode: "create" | "supersede";
  group?: NormGroup;
  productOptions: ComboboxOption[];
  operationOptions: ComboboxOption[];
  unitOptions: ComboboxOption[];
  recommendedStandardQuantity?: string;
  onClose: () => void;
  onSubmit: (values: NormFormValues) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation("masterData");
  const { t: tCommon } = useTranslation("common");
  const [values, setValues] = useState<NormFormValues>(
    mode === "supersede" && group?.current
      ? {
          ...buildEmptyForm(),
          productId: group.productId ?? "",
          operationId: group.operationId,
          standardQuantity: recommendedStandardQuantity ?? group.current.standardQuantity,
          unitId: group.current.unitId,
        }
      : buildEmptyForm(),
  );
  const [errors, setErrors] = useState<Partial<Record<keyof NormFormValues, string>>>({});

  // The base duration is never entered here — it's always the tenant's
  // CURRENT Company Performance Settings value, resolved and snapshotted
  // server-side at save time (Production Usability Fix §5-9). This is a
  // read-only preview only; the server remains authoritative and re-resolves
  // it independently (a stale/cached value shown here for a few seconds
  // between an admin changing Settings and opening this modal has no
  // correctness impact — only what the server resolves at save time is ever
  // stored).
  const settingsQuery = useQuery({
    queryKey: ["company-performance-settings", "current"],
    queryFn: () => companyPerformanceSettingsApi.current(),
  });
  // `settingsQuery.data` is `null` (not undefined/error) once the query
  // resolves and no active version exists for the tenant — see
  // company-performance-settings.controller.ts's `current` route and
  // NormsService.resolveBaseDurationMinutes, which is what actually blocks
  // save server-side. This flag surfaces that SAME precondition in the UI
  // before the form is filled out, instead of only after Save fails.
  const settingsMissing = !settingsQuery.isLoading && !settingsQuery.data;
  const navigate = useNavigate();

  function handleGoToSettings() {
    onClose();
    navigate("/podesavanja#nx-settings-worktime-section");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Defense-in-depth beyond the disabled Save button: a fast double-click/
    // double-Enter can fire before React re-renders the button as disabled
    // (isPending flips true only on the next render) — reject the second
    // submit outright so one failed/slow request never produces two toasts.
    if (isPending || settingsMissing) return;
    const schema = mode === "create" ? createNormSchema : supersedeNormSchema;
    const result = schema.safeParse(
      mode === "create"
        ? values
        : {
            standardQuantity: values.standardQuantity,
            unitId: values.unitId,
            effectiveFrom: values.effectiveFrom,
            applyRetroactively: values.applyRetroactively,
            reason: values.applyRetroactively ? values.retroactiveReason : undefined,
          },
    );
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof NormFormValues, string>> = {};
      for (const issue of result.error.issues) {
        // supersedeNormSchema's `.refine` reports under `path: ["reason"]`,
        // but the form field state key is `retroactiveReason` — map it so
        // the message actually reaches the rendered <Input>.
        const rawField = issue.path[0];
        const field = (rawField === "reason" ? "retroactiveReason" : rawField) as
          | keyof NormFormValues
          | undefined;
        if (field && !fieldErrors[field]) fieldErrors[field] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    onSubmit(values);
  }

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={mode === "create" ? t("norms.addNorm") : t("norms.newVersionOf")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tCommon("cancel")}
          </Button>
          <Button
            type="submit"
            form="nx-norm-form"
            variant="primary"
            loading={isPending}
            disabled={settingsMissing || (values.applyRetroactively && values.retroactiveReason.trim() === "")}
          >
            {tCommon("save")}
          </Button>
        </>
      }
    >
      <form id="nx-norm-form" onSubmit={handleSubmit}>
        {mode === "supersede" && group ? (
          <p className="nx-field__hint">
            {group.productName ?? t("norms.field.noProduct")} — {group.operationName}
          </p>
        ) : (
          <>
            <SearchCombobox
              label={t("norms.field.product")}
              items={productOptions}
              selectedKey={values.productId || null}
              onSelectionChange={(id) => setValues((v) => ({ ...v, productId: id ?? "" }))}
              required
              error={errors.productId}
            />
            <SearchCombobox
              label={t("norms.field.operation")}
              items={operationOptions}
              selectedKey={values.operationId || null}
              onSelectionChange={(id) => setValues((v) => ({ ...v, operationId: id ?? "" }))}
              required
              error={errors.operationId}
            />
          </>
        )}

        <Input
          label={t("norms.field.standardQuantity")}
          inputMode="decimal"
          required
          error={errors.standardQuantity}
          value={values.standardQuantity}
          onChange={(e) => setValues((v) => ({ ...v, standardQuantity: e.target.value }))}
        />
        <SearchCombobox
          label={t("norms.field.unit")}
          items={unitOptions}
          selectedKey={values.unitId || null}
          onSelectionChange={(id) => setValues((v) => ({ ...v, unitId: id ?? "" }))}
          required
          error={errors.unitId}
        />
        <DateField
          label={t("norms.field.effectiveFrom")}
          required
          error={errors.effectiveFrom}
          value={values.effectiveFrom}
          onChange={(iso) => setValues((v) => ({ ...v, effectiveFrom: iso }))}
          invalidFormatMessage={tCommon("dateField.invalidFormat")}
          calendarButtonLabel={tCommon("dateField.openCalendar")}
          previousMonthLabel={tCommon("dateField.previousMonth")}
          nextMonthLabel={tCommon("dateField.nextMonth")}
        />

        {mode === "supersede" ? (
          <div className="nx-field">
            <label className="nx-checkbox-field">
              <input
                type="checkbox"
                checked={values.applyRetroactively}
                onChange={(e) =>
                  setValues((v) => ({ ...v, applyRetroactively: e.target.checked, retroactiveReason: "" }))
                }
              />
              {t("norms.field.applyRetroactively")}
            </label>
            {values.applyRetroactively ? (
              <Input
                label={t("norms.field.retroactiveReason")}
                required
                error={errors.retroactiveReason}
                value={values.retroactiveReason}
                onChange={(e) => setValues((v) => ({ ...v, retroactiveReason: e.target.value }))}
              />
            ) : null}
          </div>
        ) : null}

        <div className="nx-field">
          <span className="nx-field__label">{t("norms.field.baseDurationMinutes")}</span>
          {settingsQuery.isLoading ? (
            <p className="nx-field__hint">{tCommon("loading")}</p>
          ) : settingsQuery.data ? (
            <p className="nx-norms__base-duration-info">
              {t("norms.field.baseDurationMinutesValue", { value: settingsQuery.data.standardAvailableMinutes })}
              <br />
              <span className="nx-field__hint">{t("norms.field.baseDurationMinutesSource")}</span>
            </p>
          ) : (
            <div className="nx-norms__settings-missing" role="status">
              <p className="nx-norms__settings-missing-title">{t("norms.field.baseDurationMinutesMissingTitle")}</p>
              <p className="nx-field__hint">{t("norms.field.baseDurationMinutesMissingBody")}</p>
              <Button type="button" variant="secondary" size="sm" onClick={handleGoToSettings}>
                <ArrowRight size={16} />
                {t("norms.field.goToSettings")}
              </Button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

export function NormsTab({
  isAdmin,
  initialRecommendedOnly,
}: {
  /** Despite the name, true for BOTH ADMIN and DIRECTOR as of the norm
   * recommendations RBAC change — the caller (MasterDataPage.tsx) computes
   * this as `canEditNorms`. Kept as `isAdmin` here only to avoid a
   * sweeping, purely-cosmetic rename across this file's ~30 existing test
   * call sites. */
  isAdmin: boolean;
  /** Pre-checks the "Samo van granica" toggle below — set when arriving
   * from the Dashboard's recommendation count card deep link. */
  initialRecommendedOnly?: boolean;
}) {
  const { t } = useTranslation("masterData");
  const { t: tCommon } = useTranslation("common");
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [formState, setFormState] = useState<FormState>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [historyData, setHistoryData] = useState<Record<string, NormHistoryItemDto[]>>({});
  const [lifecycleFilter, setLifecycleFilter] = useState<"active" | "archived">("active");
  const [deletePrompt, setDeletePrompt] = useState<NormGroup | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [recommendedOnly, setRecommendedOnly] = useState(initialRecommendedOnly ?? false);
  // Clicking a norm's "Prosek" badge reveals the sample it was computed
  // from (line/worker count) — works for every norm with a value, not just
  // ones outside bounds, so the user can judge how trustworthy the average
  // is before acting on it.
  const [sampleInfoVisible, setSampleInfoVisible] = useState<Set<string>>(new Set());

  const normsQuery = useQuery({
    queryKey: ["norms", lifecycleFilter],
    queryFn: () => normsApi.list({ includeArchived: lifecycleFilter === "archived" }),
  });
  const productsQuery = useQuery({ queryKey: ["products", "active"], queryFn: () => catalogApi.products() });
  const operationsQuery = useQuery({ queryKey: ["operations", "active"], queryFn: () => catalogApi.operations() });
  const unitsQuery = useQuery({ queryKey: ["units"], queryFn: () => catalogApi.units() });
  const recommendationsQuery = useQuery({
    queryKey: ["norm-recommendations"],
    queryFn: () => normRecommendationsApi.list(),
  });
  const recommendationByNormId = useMemo(() => {
    const map = new Map<string, NormRecommendationDto>();
    for (const r of recommendationsQuery.data ?? []) map.set(r.normId, r);
    return map;
  }, [recommendationsQuery.data]);

  const productOptions = useMemo<ComboboxOption[]>(
    () =>
      (productsQuery.data ?? []).map((p) => ({
        id: p.id,
        label: formatCodeAndName(p.code, p.canonicalName),
        businessCode: p.code,
      })),
    [productsQuery.data],
  );
  const operationOptions = useMemo<ComboboxOption[]>(
    () =>
      (operationsQuery.data ?? []).map((o) => ({
        id: o.id,
        label: formatCodeAndName(o.code, o.canonicalName),
        businessCode: o.code,
      })),
    [operationsQuery.data],
  );
  const unitOptions = useMemo<ComboboxOption[]>(
    () => (unitsQuery.data ?? []).map((u) => ({ id: u.id, label: `${u.code} — ${u.name}` })),
    [unitsQuery.data],
  );

  const groups = useMemo(
    () =>
      groupNorms(normsQuery.data ?? []).filter(
        // In the active list a `current === null` group can only be an
        // archived lineage that has been superseded at least once: its
        // archived current row is filtered out server-side (flat
        // `archivedAt: null`) while its historical rows still come back.
        // Rendering that as a row with no current version and no actions
        // is a dead-end "ghost" — drop it here; it shows under "archived".
        (g) => lifecycleFilter === "archived" || g.current !== null,
      ),
    [normsQuery.data, lifecycleFilter],
  );

  // Search-only — the active/archived dimension is already handled by the
  // pre-existing `lifecycleFilter` select above, which drives a SERVER-SIDE
  // query param (`normsApi.list({ includeArchived })`). `groups` therefore
  // only ever contains one lifecycle state at a time, unlike the
  // always-fetch-everything catalogs in MasterDataPage.tsx — so `statusOf`
  // is intentionally omitted here, and `<CatalogToolbar>` below renders with
  // `statusLabels={undefined}` (no status pills for Norms).
  const filter = useCatalogFilter(groups, {
    searchFields: [(g) => g.productName ?? "", (g) => g.operationName],
  });

  // Applied on top of useCatalogFilter's search/status result — a separate
  // concern (recommendation-flag filtering is specific to this tab, not a
  // generic catalog filter dimension).
  const recommendedOnlyFiltered = useMemo(
    () =>
      recommendedOnly
        ? filter.filtered.filter((g) => g.current && recommendationByNormId.get(g.current.id)?.isOutsideBounds)
        : filter.filtered,
    [filter.filtered, recommendedOnly, recommendationByNormId],
  );
  const recommendedCount = useMemo(
    () => groups.filter((g) => g.current && recommendationByNormId.get(g.current.id)?.isOutsideBounds).length,
    [groups, recommendationByNormId],
  );

  // Every column sortable, same `useSortableRows`/`SortableColumnHeader`
  // pattern Products/Operations/Units already use. Default matches
  // `groupNorms`'s own pre-existing default order (by operation name).
  const sortColumns = useMemo(
    () => ({
      product: (g: NormGroup) => g.productName,
      operation: (g: NormGroup) => g.operationName,
      quantity: (g: NormGroup) => (g.current ? Number(g.current.standardQuantity) : null),
      predlog: (g: NormGroup) => {
        const r = g.current ? recommendationByNormId.get(g.current.id) : undefined;
        return r?.isOutsideBounds ? Number(r.recommendedStandardQuantity) : null;
      },
      duration: (g: NormGroup) => (g.current ? Number(g.current.baseDurationMinutes) : null),
      validFrom: (g: NormGroup) => g.current?.validFrom ?? null,
      average: (g: NormGroup) => {
        const r = g.current ? recommendationByNormId.get(g.current.id) : undefined;
        return r ? Number(r.averagePerformancePercentLabel) : null;
      },
      status: (g: NormGroup) => (g.current?.archivedAt ? 1 : g.current ? 0 : 2),
      entries: (g: NormGroup) => g.current?.referencedWorkLineCount ?? null,
      minutes: (g: NormGroup) => (g.current?.totalRecordedMinutes ? Number(g.current.totalRecordedMinutes) : null),
    }),
    [recommendationByNormId],
  );
  const { sorted: sortedGroups, sortKey, direction, requestSort } = useSortableRows(
    recommendedOnlyFiltered,
    sortColumns,
    { key: "operation", direction: "asc" },
  );

  function handleError(error: unknown) {
    showToast({
      title: t("toast.saveErrorTitle"),
      description: error instanceof ApiError ? error.message : t("toast.saveErrorNoConnection"),
      tone: "danger",
    });
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateNormInput) => normsApi.create(input),
    onSuccess: () => {
      showToast({ title: t("toast.createSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["norms"] });
      setFormState(null);
    },
    onError: handleError,
  });

  const supersedeMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: SupersedeNormInput }) => normsApi.supersede(id, input),
    onSuccess: () => {
      showToast({ title: t("toast.updateSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["norms"] });
      // The lazily-fetched audit-detail cache is keyed by group and is now
      // stale (a new version/retroactive row exists) — drop it so the next
      // expand re-fetches fresh enrichment.
      setHistoryData({});
      setFormState(null);
    },
    onError: handleError,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => normsApi.archive(id),
    onSuccess: () => {
      showToast({ title: t("toast.updateSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["norms"] });
      setHistoryData({});
    },
    onError: handleError,
  });

  const unarchiveMutation = useMutation({
    mutationFn: (id: string) => normsApi.unarchive(id),
    onSuccess: () => {
      showToast({ title: t("toast.updateSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["norms"] });
      setHistoryData({});
    },
    onError: handleError,
  });

  const removeMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => normsApi.remove(id, reason),
    onSuccess: () => {
      showToast({ title: t("toast.updateSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["norms"] });
      setHistoryData({});
      setDeletePrompt(null);
      setDeleteReason("");
    },
    onError: handleError,
  });

  const isLoading = normsQuery.isLoading || productsQuery.isLoading || operationsQuery.isLoading || unitsQuery.isLoading;
  const isEmpty = !isLoading && !normsQuery.isError && groups.length === 0;
  const isPending = createMutation.isPending || supersedeMutation.isPending;

  function toggleExpanded(key: string, currentNormId: string | undefined) {
    const willExpand = !expanded.has(key);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    // Kept OUT of the setExpanded updater: a side-effect in a state updater
    // is impure (StrictMode dev double-invokes it), and the fetch needs its
    // own `.catch`. A failed history fetch deliberately leaves the base
    // rows un-enriched rather than surfacing an error (per the design).
    if (willExpand && currentNormId && !historyData[key]) {
      void normsApi
        .history(currentNormId)
        .then((rows) => {
          setHistoryData((prevData) => ({ ...prevData, [key]: rows }));
        })
        .catch(() => {});
    }
  }

  /**
   * Creator + retroactive-change detail shown on an expanded version row.
   * Used for BOTH the current version's row and each historical row so the
   * markup stays in one place.
   */
  function renderVersionDetail(detail: NormHistoryItemDto | undefined) {
    return (
      <>
        {detail?.createdBy ? (
          <span className="nx-field__hint">
            {" "}
            — {t("norms.history.createdBy")}: {detail.createdBy.email}
          </span>
        ) : null}
        {detail?.retroactive ? (
          <p className="nx-field__hint">
            {t("norms.history.retroactiveLabel")} —{" "}
            {t("norms.history.retroactiveReasonLabel")}: {detail.retroactive.reason} (
            {t("norms.history.retroactiveAffectedLabel")}: {detail.retroactive.affectedWorkLineCount})
          </p>
        ) : null}
      </>
    );
  }

  return (
    <div className="nx-master-data__tab-panel">
      <div className="nx-master-data__tab-header">
        <div className="nx-field">
          <label className="nx-field__label" htmlFor="nx-norms-lifecycle-filter">
            {t("norms.lifecycle.filterLabel")}
          </label>
          <select
            id="nx-norms-lifecycle-filter"
            className="nx-input"
            value={lifecycleFilter}
            onChange={(e) => setLifecycleFilter(e.target.value as "active" | "archived")}
          >
            <option value="active">{t("norms.lifecycle.active")}</option>
            <option value="archived">{t("norms.lifecycle.archived")}</option>
          </select>
        </div>
      </div>

      <CatalogToolbar
        filter={filter}
        searchPlaceholder={t("toolbar.searchPlaceholder.norms")}
        statusLabels={undefined}
        actions={
          <>
            <Button
              variant={recommendedOnly ? "primary" : "secondary"}
              aria-pressed={recommendedOnly}
              onClick={() => setRecommendedOnly((v) => !v)}
            >
              <AlertTriangle size={16} />
              {t("norms.recommendation.filterLabel", { count: recommendedCount })}
            </Button>
            {isAdmin ? (
              <Button variant="primary" onClick={() => setFormState({ mode: "create" })}>
                <Plus size={16} />
                {t("norms.addNorm")}
              </Button>
            ) : null}
          </>
        }
      />

      {isLoading ? (
        <p className="nx-field__hint">{tCommon("loading")}</p>
      ) : normsQuery.isError ? (
        <p className="nx-master-data__error">{t("toast.saveErrorNoConnection")}</p>
      ) : isEmpty ? (
        <div className="nx-master-data__empty">
          <p className="nx-master-data__empty-title">{t("norms.empty.title")}</p>
          <p>{isAdmin ? t("norms.empty.adminHint") : t("norms.empty.directorHint")}</p>
        </div>
      ) : sortedGroups.length === 0 ? (
        // There ARE groups, but the current search/filter matches none of
        // them — distinct from the tab having zero norms at all (the
        // `isEmpty` branch above).
        <div className="nx-master-data__empty">
          <p className="nx-master-data__empty-title">{t("toolbar.noResults")}</p>
        </div>
      ) : (
        <div className="nx-master-data__table-scroll nx-scroll-table">
          <table className="nx-master-data__table nx-table nx-norms__table">
            <thead>
              <tr>
                <SortableColumnHeader
                  label={t("norms.table.product")}
                  sortKey="product"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-norms__col-product"
                />
                <SortableColumnHeader
                  label={t("norms.table.operation")}
                  sortKey="operation"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-norms__col-operation"
                />
                <SortableColumnHeader
                  label={t("norms.table.quantity")}
                  sortKey="quantity"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-table__num nx-norms__col-narrow"
                />
                <SortableColumnHeader
                  label={t("norms.table.suggestedQuantity")}
                  sortKey="predlog"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-table__num nx-norms__col-narrow"
                />
                <SortableColumnHeader
                  label={t("norms.table.duration")}
                  sortKey="duration"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-table__num nx-norms__col-narrow"
                />
                <SortableColumnHeader
                  label={t("norms.table.entries")}
                  sortKey="entries"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-table__num nx-norms__col-narrow"
                />
                <SortableColumnHeader
                  label={t("norms.table.minutes")}
                  sortKey="minutes"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-table__num nx-norms__col-narrow"
                />
                <SortableColumnHeader
                  label={t("norms.table.validFrom")}
                  sortKey="validFrom"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                />
                <SortableColumnHeader
                  label={t("norms.table.average")}
                  sortKey="average"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                  className="nx-table__num nx-norms__col-narrow"
                />
                <SortableColumnHeader
                  label={t("norms.table.status")}
                  sortKey="status"
                  activeSortKey={sortKey}
                  direction={direction}
                  onSort={requestSort}
                />
                <th className="nx-norms__col-actions" aria-label={t("norms.table.actions")} />
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map((group) => {
                const isExpanded = expanded.has(group.key);
                // The SUPERSEDE_RETROACTIVE audit row is recorded against the
                // NEW norm row, which stays the CURRENT version until itself
                // superseded — so its detail must render on the current row's
                // expansion, not only once it becomes historical.
                const currentDetail =
                  isExpanded && group.current
                    ? historyData[group.key]?.find((d) => d.normId === group.current!.id)
                    : undefined;
                const recommendation = group.current ? recommendationByNormId.get(group.current.id) : undefined;
                const deleteBlocked =
                  group.history.length > 0 || (group.current?.referencedWorkLineCount ?? 0) > 0;
                const menuItems: OverflowMenuItem[] = [];
                if (group.history.length > 0) {
                  menuItems.push({
                    key: "history",
                    label: expanded.has(group.key) ? t("norms.history.hide") : t("norms.history.show"),
                    icon: <History size={14} aria-hidden />,
                    onClick: () => toggleExpanded(group.key, group.current?.id ?? group.history[0]?.id),
                  });
                }
                if (isAdmin && group.current && !group.current.archivedAt) {
                  menuItems.push({
                    key: "supersede",
                    label: t("norms.newVersion"),
                    icon: <GitBranch size={14} aria-hidden />,
                    onClick: () =>
                      setFormState({
                        mode: "supersede",
                        group,
                        recommendedStandardQuantity: recommendation?.recommendedStandardQuantity,
                      }),
                  });
                  menuItems.push({
                    key: "archive",
                    label: t("norms.lifecycle.archiveAction"),
                    icon: <Archive size={14} aria-hidden />,
                    disabled: archiveMutation.isPending,
                    onClick: () => archiveMutation.mutate(group.current!.id),
                  });
                } else if (isAdmin && group.current?.archivedAt) {
                  menuItems.push({
                    key: "unarchive",
                    label: t("norms.lifecycle.unarchiveAction"),
                    icon: <ArchiveRestore size={14} aria-hidden />,
                    disabled: unarchiveMutation.isPending,
                    onClick: () => unarchiveMutation.mutate(group.current!.id),
                  });
                }
                if (isAdmin && group.current) {
                  // Matches NormsService.remove's own server-side guard
                  // exactly: blocked by EITHER version history OR the
                  // current row itself having been referenced by any
                  // WorkLine — history.length alone missed the single-
                  // version-but-already-used case (looked deletable, server
                  // rejected it).
                  menuItems.push({
                    key: "delete",
                    label: t("norms.lifecycle.deleteAction"),
                    icon: <Trash2 size={14} aria-hidden />,
                    tone: "danger",
                    disabled: deleteBlocked,
                    title: deleteBlocked ? t("norms.lifecycle.deleteBlockedHint") : undefined,
                    onClick: () => setDeletePrompt(group),
                  });
                }
                return (
                <Fragment key={group.key}>
                  <tr>
                    <td className="nx-norms__col-product" title={group.productName ?? undefined}>
                      {group.productName ?? t("norms.field.noProduct")}
                    </td>
                    <td className="nx-norms__col-operation" title={group.operationName}>
                      {group.operationName}
                    </td>
                    <td className="nx-table__num nx-norms__col-narrow">
                      {formatNum(group.current?.standardQuantity)}
                    </td>
                    <td className="nx-table__num nx-norms__col-narrow">
                      {recommendation?.isOutsideBounds
                        ? formatNum(recommendation.recommendedStandardQuantity)
                        : "—"}
                    </td>
                    <td className="nx-table__num nx-norms__col-narrow">
                      {formatNum(group.current?.baseDurationMinutes)}
                    </td>
                    <td className="nx-table__num nx-norms__col-narrow">
                      {formatNum(group.current?.referencedWorkLineCount)}
                    </td>
                    <td className="nx-table__num nx-norms__col-narrow">
                      {formatNum(group.current?.totalRecordedMinutes)}
                    </td>
                    <td>{group.current?.validFrom ? formatDateSr(group.current.validFrom) : "—"}</td>
                    <td className="nx-table__num nx-norms__col-narrow">
                      {recommendation ? (
                        <>
                          <button
                            type="button"
                            className="nx-norms__average-trigger"
                            aria-expanded={sampleInfoVisible.has(group.key)}
                            onClick={() =>
                              setSampleInfoVisible((prev) => {
                                const next = new Set(prev);
                                if (next.has(group.key)) next.delete(group.key);
                                else next.add(group.key);
                                return next;
                              })
                            }
                          >
                            <Badge tone={recommendation.isOutsideBounds ? "warning" : "neutral"}>
                              {recommendation.averagePerformancePercentLabel}%
                            </Badge>
                          </button>
                          {sampleInfoVisible.has(group.key) ? (
                            <p className="nx-field__hint nx-norms__average-sample">
                              {t("norms.recommendation.sampleInfo", {
                                lines: recommendation.sampleLineCount,
                                workers: recommendation.sampleWorkerCount,
                              })}
                            </p>
                          ) : null}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {group.current?.archivedAt ? (
                        <Badge tone="neutral" icon={<Archive size={12} aria-hidden />}>
                          {t("norms.lifecycle.archivedBadge")}
                        </Badge>
                      ) : group.current ? (
                        <Badge tone="success" icon={<CheckCircle2 size={12} aria-hidden />}>
                          {t("norms.status.current")}
                        </Badge>
                      ) : (
                        <Badge tone="neutral">—</Badge>
                      )}
                    </td>
                    <td className="nx-norms__col-actions">
                      {menuItems.length > 0 ? (
                        <OverflowMenu items={menuItems} triggerLabel={t("norms.table.actions")} />
                      ) : null}
                    </td>
                  </tr>
                  {isExpanded ? (
                    <>
                      {group.current && currentDetail ? (
                        <tr key={`${group.current.id}-current`} className="nx-norms__history-row">
                          <td colSpan={2} className="nx-norms__history-label">
                            {t("norms.status.current")}
                            {renderVersionDetail(currentDetail)}
                          </td>
                          <td className="nx-table__num">{formatNum(group.current.standardQuantity)}</td>
                          <td />
                          <td className="nx-table__num">{formatNum(group.current.baseDurationMinutes)}</td>
                          <td className="nx-table__num">
                            {formatNum(group.current.referencedWorkLineCount)}
                          </td>
                          <td className="nx-table__num">
                            {formatNum(group.current.totalRecordedMinutes)}
                          </td>
                          <td>{formatValidRange(group.current.validFrom, group.current.validTo)}</td>
                          <td />
                          <td />
                          <td />
                        </tr>
                      ) : null}
                      {group.history.map((h) => {
                        const detail = historyData[group.key]?.find((d) => d.normId === h.id);
                        return (
                          <tr key={h.id} className="nx-norms__history-row">
                            <td colSpan={2} className="nx-norms__history-label">
                              {t("norms.status.historical")}
                              {renderVersionDetail(detail)}
                            </td>
                            <td className="nx-table__num">{formatNum(h.standardQuantity)}</td>
                            <td />
                            <td className="nx-table__num">{formatNum(h.baseDurationMinutes)}</td>
                            <td className="nx-table__num">{formatNum(h.referencedWorkLineCount)}</td>
                            <td className="nx-table__num">{formatNum(h.totalRecordedMinutes)}</td>
                            <td>{formatValidRange(h.validFrom, h.validTo)}</td>
                            <td />
                            <td />
                            <td />
                          </tr>
                        );
                      })}
                    </>
                  ) : null}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && formState ? (
        <NormFormModal
          mode={formState.mode}
          group={formState.mode === "supersede" ? formState.group : undefined}
          productOptions={productOptions}
          operationOptions={operationOptions}
          unitOptions={unitOptions}
          recommendedStandardQuantity={formState.mode === "supersede" ? formState.recommendedStandardQuantity : undefined}
          onClose={() => setFormState(null)}
          isPending={isPending}
          onSubmit={(values) => {
            if (formState.mode === "create") {
              createMutation.mutate({
                productId: values.productId,
                operationId: values.operationId,
                standardQuantity: values.standardQuantity,
                unitId: values.unitId,
                effectiveFrom: values.effectiveFrom,
              });
            } else if (formState.mode === "supersede" && formState.group.current) {
              supersedeMutation.mutate({
                id: formState.group.current.id,
                input: {
                  standardQuantity: values.standardQuantity,
                  unitId: values.unitId,
                  effectiveFrom: values.effectiveFrom,
                  applyRetroactively: values.applyRetroactively,
                  reason: values.applyRetroactively ? values.retroactiveReason : undefined,
                },
              });
            }
          }}
        />
      ) : null}

      {isAdmin && deletePrompt ? (
        <Modal
          open
          onOpenChange={(open) => {
            if (!open) {
              setDeletePrompt(null);
              setDeleteReason("");
            }
          }}
          title={t("norms.lifecycle.deleteConfirmTitle")}
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setDeletePrompt(null);
                  setDeleteReason("");
                }}
              >
                {tCommon("cancel")}
              </Button>
              <Button
                variant="danger"
                disabled={deleteReason.trim() === "" || removeMutation.isPending}
                onClick={() => removeMutation.mutate({ id: deletePrompt.current!.id, reason: deleteReason })}
              >
                {t("norms.lifecycle.deleteConfirmTitle")}
              </Button>
            </>
          }
        >
          <p>{t("norms.lifecycle.deleteConfirmBody")}</p>
          <Input
            label={t("norms.lifecycle.deleteReasonLabel")}
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
        </Modal>
      ) : null}
    </div>
  );
}
